import BrowserDetector from '@/scripts/helpers/browser-detector';
import mimeTypeHelper from '@/scripts/helpers/mime-type-helper';
import { Model } from '@viddsee/server-api-client';
import { Nullable } from '@/scripts/models/utility-types';

export enum VideoSourceProvider {
  Vimeo = 'vimeo',
  Hosted = 'hosted',
}
export type QualityLabel = 'Auto' | number;

export interface VideoSourceResponseSubtitle {
  name: string;
  file_url: string;
}
export interface VideoSourceResponseSourceBase {
  id: number;
  source_type: string;
  url: string;
}
export interface VideoSourceResponseSourceHls extends VideoSourceResponseSourceBase {
  source_type: 'hosted_hls';
  'CloudFront-Signature': string;
  'CloudFront-Key-Pair-Id': string;
  'CloudFront-Policy': string;
}

export type VideoSourceResponseSource = VideoSourceResponseSourceBase | VideoSourceResponseSourceHls

export interface VideoSourceResponse {
  subtitles: VideoSourceResponseSubtitle[];
  sources: VideoSourceResponseSource[];
}

export interface VideoSourcePayloadTrack {
  label: string;
  file: string;
  kind: 'captions';
}

export interface GeneratedVideoSourcePayloadSourceMeta {
  provider: VideoSourceProvider;
  source: string;
  quality: string | number;
  signatureCookieHeaders: Nullable<string[]>;
  mimeType: string;
}
export interface VideoSourcePayloadSource {
  label: string;
  file: string;
  meta: GeneratedVideoSourcePayloadSourceMeta;
  default?: boolean;
}

export type GeneratedVideoLabelSourcesMap = {
  [label in QualityLabel]?: VideoSourcePayloadSource;
}

export type GeneratedVideoProviderLabelSourcesMap = {
  [provider in VideoSourceProvider]?: GeneratedVideoLabelSourcesMap;
}

export interface GeneratedVideoSourcePayload {
  title: string;
  description: string;
  image: string;
  url: string;
  mediaid: string;
  tracks: VideoSourcePayloadTrack[];
  sources: VideoSourcePayloadSource[];
  withCredentials: any;
}

const COOKIE_REQUEST_PATH_REGEX = /.*\.com(\/(dev\/)?.*\/hls)/;

// Newer source will be labeled with numeric value.
// `mapLegacyQualityLabel` will attempt to map existing named quality label to numeric label instead
const LEGACY_MAP = {
  [VideoSourceProvider.Vimeo]: {
    uhd: 1080,
    hd: 720,
    sd: 540,
    mo: 360,
    hls: 'Auto',
  },
  [VideoSourceProvider.Hosted]: {
    uhd: 1080,
    hd: 720,
    sd: 480,
    mo: 360,
    hls: 'Auto',
  },
};
const mapLegacyQualityLabel = (provider: VideoSourceProvider, qualityLabel: string): QualityLabel | string => {
  const label = ((LEGACY_MAP as any)[provider] || {})[qualityLabel];
  return label || qualityLabel;
};

// HLS signing headers
const HLS_SIGNING_HEADER = [
  'CloudFront-Key-Pair-Id',
  'CloudFront-Policy',
  'CloudFront-Signature',
];
const PROVIDER_PREFERENCE_RANK = [
  VideoSourceProvider.Vimeo,
  VideoSourceProvider.Hosted,
];
const SOURCE_PROVIDER_MAP = {
  vimeo: 'vimeo_mp4',
  hosted: 'hosted',
};

// Methods
const methods = {
  makeSigningCookieHeader(source: VideoSourceResponseSource) {
    return (property: string): Nullable<string[]> => {
      const value = (source as any)[property];
      if (!value) return null;
      return [property, (source as any)[property], source.url];
    };
  },

  flattenSourceMap(sourceMap: GeneratedVideoLabelSourcesMap): VideoSourcePayloadSource[] {
    return Object.entries(sourceMap)
      .map(([, source]) => source)
      .filter(x => x) as VideoSourcePayloadSource[]
    ;
  },

  getSourceName(provider: string): string {
    return (SOURCE_PROVIDER_MAP as any)[provider] || provider;
  },

  /**
   * Create a map with the structure of > provider > qualities
   *
   * Example:
   * {
   *   vimeo:
   *     1080: [Object object]
   *   hosted:
   *     1080: [Object object]
   * }
   *
   * @param {Object} sources obtained from the server
   * @return {Object} a map containing >providers>qualities
   */
  generateSourceMap(sources: any[]): GeneratedVideoProviderLabelSourcesMap {
    return sources
      .reduce((
        acc,
        source: VideoSourceResponseSource,
      ) => {
        const [provider, qualityLabel] = source.source_type.split('_');

        // Get label: Support legacy quality
        const label = mapLegacyQualityLabel(provider as VideoSourceProvider, qualityLabel);

        // Get signature
        const signatureCookieHeaders = HLS_SIGNING_HEADER
          .map(methods.makeSigningCookieHeader(source))
          .filter(entries => entries !== null);
        const needCookieSigning = signatureCookieHeaders.length;

        // Create source map
        acc[provider] = acc[provider] || {};
        acc[provider][label] = {
          label: ((typeof label === 'number') || parseInt(label, 10))
            ? `${label}p`
            : label
          ,
          file: source.url,
          meta: {
            provider,
            source: methods.getSourceName(provider),
            quality: label,
            signatureCookieHeaders: needCookieSigning ? signatureCookieHeaders : null,
            mimeType: mimeTypeHelper.parse(source.url),
          },
        };

        // Finalize reduction for this source
        return acc;
      }, Object.create(null) as GeneratedVideoProviderLabelSourcesMap);
  },

  /**
   * Mapper method for mapping server source to player source
   * @param {Object} subtitle
   * @return {Object} subtitle source usable for JwPlayer
   */
  toPlayerSubtitleSources(subtitle: VideoSourceResponseSubtitle): VideoSourcePayloadTrack {
    return ({
      label: subtitle.name,
      file: subtitle.file_url,
      kind: 'captions',
    });
  },

  /**
   * Sort quality label by descending order
   * This method make use of Numerical quality label for comparison between quality
   * @param {String} apple - Stands for A
   * @param {String} pineapple - Stands for B (pine... bine... pine...)
   */
  toDescendingQuality(apple: VideoSourcePayloadSource, pineapple: VideoSourcePayloadSource): number {
    const PUT_PINEAPPLE_THEN_APPLE = 1;
    const PUT_APPLE_THEN_PINEAPPLE = -1;

    // Extract quality label as numerical value for comparison
    const appleQualityLabel = parseInt(`${apple.meta.quality}`, 10);
    const pineappleQualityLabel = parseInt(`${pineapple.meta.quality}`, 10);

    // If quality label cannot be resolved to numeric label, always sort it to the top
    // This case happen when label is a hls stream with label "Auto"
    if (!appleQualityLabel) return PUT_APPLE_THEN_PINEAPPLE;
    if (!pineappleQualityLabel) return PUT_PINEAPPLE_THEN_APPLE;

    // Otherwise, compare between the numerical value
    const isAppleBiggerThanPineapple = (appleQualityLabel || 0) > (pineappleQualityLabel || 0);
    return isAppleBiggerThanPineapple ? PUT_APPLE_THEN_PINEAPPLE : PUT_PINEAPPLE_THEN_APPLE;
  },

  handleCookieSigning(source: VideoSourcePayloadSource): boolean {
    const signatureCookieHeaders = source.meta.signatureCookieHeaders;
    if (!signatureCookieHeaders) return false;

    // A cookie signing is required. Perform cookie allocation here
    signatureCookieHeaders.forEach(([key, value, file]) => {
      const matchedCookiePath = file.match(COOKIE_REQUEST_PATH_REGEX);
      const cookiePath = matchedCookiePath ? matchedCookiePath[1] : '/';
      document.cookie = `${key}=${value} ;path=${cookiePath}; domain=.viddsee.com`;
    });
    return true;
  },

  mutateSourceForDefaultHls(source: VideoSourcePayloadSource): boolean {
    if (source.meta.quality !== 'Auto') return false;
    source.default = true;
    return true;
  },
  mutateSourceForDefaultHostedQuality(source: VideoSourcePayloadSource): boolean {
    const quality = parseInt(`${source.meta.quality}`, 10);
    if (!quality) return false;

    // Default video resolution should be smaller or equal to native screen width
    const nativeScreenWidth = window.screen.width * window.devicePixelRatio;
    if (quality > nativeScreenWidth) {
      return false;
    }

    // Special guard for mobile. Mobile shall use lower resolution
    const isMobile = (
      (BrowserDetector.os() === 'AndroidOS') ||
      (BrowserDetector.os() === 'iOS' && BrowserDetector.md().phone() === 'iPhone')
    );
    if (isMobile && quality > 600) {
      return false;
    }

    source.default = true;
    return true;
  },
};

export default {
  generate(data: VideoSourceResponse, video: Model.Video.Info): GeneratedVideoSourcePayload {
    const tracks = data.subtitles.map(methods.toPlayerSubtitleSources);
    const sourceMap = methods.generateSourceMap(data.sources);

    const preferredProviderName = PROVIDER_PREFERENCE_RANK.find(providerName => !!sourceMap[providerName]);
    if (!preferredProviderName) throw new Error('Unable to obtain a valid video source provider');

    const sources = methods
      .flattenSourceMap(sourceMap[preferredProviderName] as GeneratedVideoLabelSourcesMap)
      .sort(methods.toDescendingQuality as any /* see: https://github.com/microsoft/TypeScript/issues/34099 */);

    // Handle cookie signing
    const withCredentials = !!sources.find(methods.handleCookieSigning); // only work for first available source

    // Set default quality
    sources.find(methods.mutateSourceForDefaultHostedQuality);
    sources.find(methods.mutateSourceForDefaultHls);

    return ({
      title: video.title,
      description: video.headline,
      image: video.photoLargeUrl,
      url: `${location.origin}/video/${video.slug}/${video.id}`,
      mediaid: video.id,
      tracks,
      sources,
      withCredentials,
    });
  },
};
